// FeatureToggles — Sprint 39. Per-condo premium-feature management
// surface for super-admins. This is the canonical /admin/features
// UI promised in the Sprint 39-onwards roadmap; every Sprint 40+
// capability registers a featureKey here so super-admins can grant
// access without a deploy.
//
// Distinct from FeaturePermissions.tsx — that one controls per-role
// SIDEBAR VISIBILITY (UI surfacing). This one controls per-condo
// CAPABILITY GATES that the backend enforces via PaymentFeatureGateService.
//
// Backend contract: GET/POST /api/condo-features/{activate,deactivate}
// already wired (see CondoCorpFeatureController).
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Switch, Tag, Input, Space, Typography, Select, Empty,
  Spin, Tooltip, Button, Popconfirm, Alert, Tabs,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  ApiOutlined, BankOutlined, CalendarOutlined, CreditCardOutlined,
  DollarOutlined, FileTextOutlined, MessageOutlined, ReloadOutlined,
  SafetyCertificateOutlined, SearchOutlined, SettingOutlined,
  TeamOutlined, ToolOutlined, ImportOutlined, MobileOutlined,
  WarningOutlined, ShopOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { useCondoStore } from '../store/condoStore';
import { apiService } from '../services/api';
import type { CondoCorpFeature } from '../types';

import { formatDateTime } from '../utils/date';
const { Title, Text } = Typography;
const { Option } = Select;

// ─────────────────────────────────────────────────────────────────────
// Feature catalogue — single source of truth in the frontend for
// what every featureKey means in plain English. New sprints add rows
// here (and to the V8 seed migration + the conventions doc).
// ─────────────────────────────────────────────────────────────────────

interface FeatureMeta {
  key:        string;
  name:       string;
  area:       FeatureArea;
  sprint:     number;        // sprint that ships the underlying capability
  summary:    string;        // one-sentence explanation for the super-admin
  icon:       React.ReactNode;
}

type FeatureArea =
  | 'Payments' | 'Finance' | 'Reports' | 'Maintenance' | 'Vendors'
  | 'Governance' | 'Amenity' | 'Documents' | 'Firm & Portfolio'
  | 'Communications' | 'Onboarding' | 'Front Desk' | 'Integrations'
  | 'Platform' | 'Support';

const AREA_ICONS: Record<FeatureArea, React.ReactNode> = {
  Payments:           <CreditCardOutlined />,
  Finance:            <DollarOutlined />,
  Reports:            <FileTextOutlined />,
  Maintenance:        <ToolOutlined />,
  Vendors:            <ShopOutlined />,
  Governance:         <SafetyCertificateOutlined />,
  Amenity:            <CalendarOutlined />,
  Documents:          <FileTextOutlined />,
  'Firm & Portfolio': <BankOutlined />,
  Communications:     <MessageOutlined />,
  Onboarding:         <ImportOutlined />,
  'Front Desk':       <TeamOutlined />,
  Integrations:       <ApiOutlined />,
  Platform:           <MobileOutlined />,
  Support:            <SettingOutlined />,
};

const FEATURE_CATALOG: FeatureMeta[] = [
  // Sprint 26 — already in production
  { key: 'payment_card_processing', area: 'Payments', sprint: 26, name: 'Card Processing (Stripe)',
    summary: 'Resident-facing online card payments via Stripe. Enables the "Pay by Card" tab in Payment Centre.',
    icon: <CreditCardOutlined /> },
  { key: 'payment_pad_debit', area: 'Payments', sprint: 26, name: 'Pre-Authorized Debit (PAD)',
    summary: 'Canadian PAD enrollment workflow under Payments Canada Rule H1. UI placeholder today; automated debit lands in Sprint 40.',
    icon: <CreditCardOutlined /> },
  { key: 'payment_bulk_upload', area: 'Payments', sprint: 26, name: 'Bulk Payment Upload',
    summary: 'Admin CSV upload to record many payments at once. Includes row-level validation.',
    icon: <CreditCardOutlined /> },

  // Sprint 40 — Payments parity
  { key: 'payment_ach', area: 'Payments', sprint: 40, name: 'ACH / Bank Debit',
    summary: 'Automated bank-account debit via Stripe ACSS. Requires PAD-compliant mandate from the resident.',
    icon: <CreditCardOutlined /> },
  { key: 'payment_autopay', area: 'Payments', sprint: 40, name: 'Resident Auto-Pay',
    summary: 'Residents enroll once; monthly fees clear automatically on the due date.',
    icon: <CreditCardOutlined /> },
  { key: 'payment_refunds', area: 'Payments', sprint: 40, name: 'Refund Flow',
    summary: 'Admin-triggered full or partial refunds via Stripe with reason + audit trail.',
    icon: <CreditCardOutlined /> },

  // Sprint 41 — Tax + auto-match
  { key: 'finance_tax_hst_gst', area: 'Finance', sprint: 41, name: 'HST / GST / PST Tax Module',
    summary: 'Province-aware tax calculation on taxable fees. Statements and AR aging show tax as a separate line.',
    icon: <DollarOutlined /> },
  { key: 'payment_auto_match', area: 'Payments', sprint: 41, name: 'Bulk Payment Auto-Match',
    summary: 'Bulk-imported payments match against open fees by unit + amount; ambiguous rows go to manual review.',
    icon: <CreditCardOutlined /> },

  // Sprint 42 — Reporting finish
  { key: 'reports_cash_flow_forecast', area: 'Reports', sprint: 42, name: 'Cash Flow Forecast',
    summary: 'Server-side 12-month cash flow projection. Replaces client-only computation.',
    icon: <FileTextOutlined /> },
  { key: 'reports_server_pdf', area: 'Reports', sprint: 42, name: 'Server-Side Report PDFs',
    summary: 'All reports render as PDFs on the server via PDFBox. Pixel-perfect, identical across browsers.',
    icon: <FileTextOutlined /> },
  { key: 'reports_reserve_forecast', area: 'Reports', sprint: 42, name: 'Reserve Fund 20-Year Forecast',
    summary: 'Year-by-year projected reserve balance against a 20-year capital project schedule.',
    icon: <FileTextOutlined /> },

  // Sprint 43 — Maintenance + violations
  { key: 'maintenance_photos', area: 'Maintenance', sprint: 43, name: 'Maintenance Request Photos',
    summary: 'Residents attach photos to maintenance requests via presigned S3 upload.',
    icon: <ToolOutlined /> },
  { key: 'maintenance_recurring', area: 'Maintenance', sprint: 43, name: 'Recurring Maintenance Schedules',
    summary: 'HVAC, pool, fire panel — recurring requests spawn automatically on their cadence.',
    icon: <ToolOutlined /> },
  { key: 'vendor_sla_scoring', area: 'Vendors', sprint: 43, name: 'Vendor SLA Scorecard',
    summary: 'Auto-computed time-to-completion, on-time %, and average rating per vendor.',
    icon: <ShopOutlined /> },
  { key: 'violations_pdf_export', area: 'Maintenance', sprint: 43, name: 'Violation Notice PDF',
    summary: 'Generate a printable notice for any violation with photo, clause, fine, and response window.',
    icon: <WarningOutlined /> },

  // Sprint 44 — Status cert + AGM packet
  { key: 'status_cert_email', area: 'Governance', sprint: 44, name: 'Status Certificate Email Delivery',
    summary: 'Email a generated Status Certificate PDF directly to the requestor; logged in Communication History.',
    icon: <SafetyCertificateOutlined /> },
  { key: 'status_cert_signatures', area: 'Governance', sprint: 44, name: 'Status Certificate E-Signatures',
    summary: 'Authorized officers sign a Status Certificate by drawing or typing; the signature renders on the issued PDF.',
    icon: <SafetyCertificateOutlined /> },
  { key: 'status_cert_public_requests', area: 'Governance', sprint: 133, name: 'Public Status Certificate Requests',
    summary: 'Expose a public link where an external requestor (buyer\'s solicitor) can request a status certificate without a login; creates an in-app request + notifies the manager.',
    icon: <SafetyCertificateOutlined /> },
  { key: 'agm_packet_bulk', area: 'Governance', sprint: 44, name: 'AGM Packet Bulk Generation',
    summary: 'One-click AGM packet PDF: notice, minutes, financials, reserve, auditor report, resolutions, proxy.',
    icon: <SafetyCertificateOutlined /> },

  // Sprint 45 — E-voting
  { key: 'governance_evoting', area: 'Governance', sprint: 45, name: 'Online E-Voting + Quorum',
    summary: 'Board resolutions open for asynchronous voting between meetings with live quorum tracking.',
    icon: <SafetyCertificateOutlined /> },

  // Sprint 46 — Amenity completeness
  { key: 'amenity_deposit', area: 'Amenity', sprint: 46, name: 'Amenity Deposits',
    summary: 'Stripe-captured deposit at booking time. Refunded on policy-compliant cancellation.',
    icon: <CalendarOutlined /> },
  { key: 'amenity_cancellation_policy', area: 'Amenity', sprint: 46, name: 'Amenity Cancellation Policy',
    summary: 'Per-amenity refund rules (48h full / 24h partial / under 24h none). Auto-applied on cancel.',
    icon: <CalendarOutlined /> },
  { key: 'amenity_blackouts', area: 'Amenity', sprint: 46, name: 'Amenity Blackout Windows',
    summary: 'Admin blocks dates for maintenance / private events. Bookings rejected during blackouts.',
    icon: <CalendarOutlined /> },
  { key: 'amenity_waitlist', area: 'Amenity', sprint: 46, name: 'Amenity Waitlist',
    summary: 'Residents waitlist a full slot; auto-notified on cancellation with a 4-hour confirmation window.',
    icon: <CalendarOutlined /> },

  // Sprint 47 — Documents
  { key: 'documents_folder_tree', area: 'Documents', sprint: 47, name: 'Document Folder Tree',
    summary: 'Hierarchical folders replace flat document type. Standard folders seeded per condo.',
    icon: <FileTextOutlined /> },
  { key: 'documents_access_levels', area: 'Documents', sprint: 47, name: 'Document Access Levels',
    summary: 'Per-document board-only / owners-only / all-residents / public visibility.',
    icon: <FileTextOutlined /> },

  // Sprint 48 — Firm-tier
  { key: 'firm_workspace', area: 'Firm & Portfolio', sprint: 48, name: 'Management Firm Workspace',
    summary: 'Firm-admin role spans every condo under a management company. Distinct from per-condo super-admin.',
    icon: <BankOutlined /> },
  { key: 'portfolio_dashboard', area: 'Firm & Portfolio', sprint: 48, name: 'Portfolio Dashboard',
    summary: 'Roll-up KPIs across every condo a user has access to — open work orders, collections, occupancy.',
    icon: <BankOutlined /> },
  { key: 'vendor_portfolio_scoring', area: 'Firm & Portfolio', sprint: 48, name: 'Portfolio Vendor Scoring',
    summary: 'Vendor SLA scores aggregated across every condo in the portfolio.',
    icon: <ShopOutlined /> },
  { key: 'onboarding_templates', area: 'Firm & Portfolio', sprint: 48, name: 'Property Onboarding Templates',
    summary: 'Firm-admin saves "standard new-condo setup"; new condos provision from a template in 2 days.',
    icon: <BankOutlined /> },

  // Sprint 49 — PWA
  { key: 'pwa_install_prompt', area: 'Platform', sprint: 49, name: 'PWA Install Prompt',
    summary: 'Show the "Install CondoPro" banner to residents on mobile browsers. Manifest + service worker always active.',
    icon: <MobileOutlined /> },

  // Sprint 51 — Communications
  { key: 'comms_multichannel_composer', area: 'Communications', sprint: 51, name: 'Multi-Channel Composer',
    summary: 'Send one announcement across email + SMS + push from a single composer with live preview.',
    icon: <MessageOutlined /> },
  { key: 'comms_sms', area: 'Communications', sprint: 51, name: 'SMS Notifications (Twilio)',
    summary: 'Residents who opt in receive SMS for critical announcements. Per-condo daily throttle to prevent flooding.',
    icon: <MessageOutlined /> },
  { key: 'comms_push', area: 'Communications', sprint: 51, name: 'Push Notifications',
    summary: 'iOS + Android push to the PWA-installed app. FCM + APNs under the hood.',
    icon: <MessageOutlined /> },
  { key: 'comms_announcement_templates', area: 'Communications', sprint: 51, name: 'Announcement Templates',
    summary: 'Save reusable templates (Pool closure, Lobby cleaning, Fee reminder). Pick + customise.',
    icon: <MessageOutlined /> },

  // Sprint 52 — Onboarding & import
  { key: 'import_unit_csv', area: 'Onboarding', sprint: 52, name: 'Unit Roster CSV Import',
    summary: 'Auto-mapping CSV import for the unit roster. Dry-run preview + row-level errors.',
    icon: <ImportOutlined /> },
  { key: 'import_historical_financials', area: 'Onboarding', sprint: 52, name: 'Historical Financials Import',
    summary: 'Bulk-import past fees and payments from QuickBooks-style CSV exports.',
    icon: <ImportOutlined /> },
  { key: 'onboarding_wizard_self_serve', area: 'Onboarding', sprint: 52, name: 'Self-Serve Onboarding Wizard',
    summary: 'Sign up → confirm email → create firm → create condo → invite first board member. No sales call.',
    icon: <ImportOutlined /> },

  // Sprint 53 — Front desk
  { key: 'frontdesk_visitor_qr', area: 'Front Desk', sprint: 53, name: 'Visitor QR Pre-Auth',
    summary: 'Pre-authorised visitors receive a one-time QR for front-desk / door-reader redemption.',
    icon: <TeamOutlined /> },
  { key: 'frontdesk_lobby_view', area: 'Front Desk', sprint: 53, name: 'Lobby Dashboard',
    summary: 'Concierge view: today\'s expected visitors, package log, redeem-QR action.',
    icon: <TeamOutlined /> },

  // Sprint 54 — Integrations 1
  { key: 'integration_qbo', area: 'Integrations', sprint: 54, name: 'QuickBooks Online Sync',
    summary: 'Two-way sync of payments, fees, and expenses with QuickBooks Online via Intuit\'s OAuth2 connector.',
    icon: <ApiOutlined /> },
  { key: 'integration_bank_sync', area: 'Integrations', sprint: 54, name: 'Bank Account Sync (Flinks/Plaid)',
    summary: 'Read-only bank account feed for daily reconciliation against recorded payments.',
    icon: <ApiOutlined /> },

  // Sprint 55 — Integrations 2
  { key: 'integration_access_control', area: 'Integrations', sprint: 55, name: 'Access Control (ButterflyMX)',
    summary: 'Push visitor authorisations to ButterflyMX lobby system; pull entry events into the audit log.',
    icon: <ApiOutlined /> },
  { key: 'integration_calendar', area: 'Integrations', sprint: 55, name: 'Calendar (Google / Outlook)',
    summary: 'Confirmed amenity bookings appear on the resident\'s connected calendar. ICS feed for admins.',
    icon: <ApiOutlined /> },

  // Sprint 56 — Integrations 3
  { key: 'integration_saml_sso', area: 'Integrations', sprint: 56, name: 'SAML 2.0 SSO',
    summary: 'Firm-admin SSO via Okta / Azure AD. Firm-tier feature; not available on Board / Manager plans.',
    icon: <ApiOutlined /> },
  { key: 'integration_scim', area: 'Integrations', sprint: 56, name: 'SCIM Provisioning',
    summary: 'Auto-provision users into condos based on IdP group membership. Pairs with SAML.',
    icon: <ApiOutlined /> },

  // Sprint 60 — Support
  { key: 'support_inapp_chat', area: 'Support', sprint: 60, name: 'In-App Live Chat',
    summary: 'Embedded HelpScout/Intercom chat widget for admin pages. Resident channel remains email.',
    icon: <SettingOutlined /> },
];

// ─────────────────────────────────────────────────────────────────────

export const FeatureToggles: React.FC = () => {
  const { user, currentCondo } = useAuthStore();
  const { condos, fetchCondos } = useCondoStore();

  const [selectedCondoId, setSelectedCondoId] = useState<string | undefined>(currentCondo?.id);
  const [rows,    setRows]    = useState<CondoCorpFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');
  const [areaTab, setAreaTab] = useState<string>('all');
  const [toggling, setToggling] = useState<Record<string, boolean>>({});

  const isSuperAdmin = (user?.role as string) === 'super_admin_condo'
    || (user?.role as string) === 'superadmin';

  useEffect(() => {
    if (!condos?.length) fetchCondos();
  }, [condos?.length, fetchCondos]);

  useEffect(() => {
    if (!selectedCondoId) return;
    let cancelled = false;
    setLoading(true);
    apiService.getCondoFeatures(selectedCondoId)
      .then(data => { if (!cancelled) setRows(data); })
      .catch(() => { if (!cancelled) staticApi.message.error('Failed to load feature flags for this condo.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCondoId]);

  // Index existing rows by key for the merge below
  const rowByKey = useMemo(() => {
    const m: Record<string, CondoCorpFeature> = {};
    rows.forEach(r => { m[r.featureKey] = r; });
    return m;
  }, [rows]);

  // Compose catalogue + state. Rows in DB but not in catalogue are
  // still rendered (forward-compat for sprints that add a key before
  // updating this file).
  const composed = useMemo(() => {
    const catalogueKeys = new Set(FEATURE_CATALOG.map(f => f.key));
    const catalogueRows = FEATURE_CATALOG.map(meta => ({
      ...meta,
      enabled:     rowByKey[meta.key]?.enabled ?? false,
      activatedAt: rowByKey[meta.key]?.activatedAt,
      activatedBy: rowByKey[meta.key]?.activatedBy,
      hasRow:      Boolean(rowByKey[meta.key]),
    }));
    const orphanRows = rows
      .filter(r => !catalogueKeys.has(r.featureKey))
      .map(r => ({
        key:     r.featureKey,
        name:    r.featureKey,
        area:    'Platform' as FeatureArea,
        sprint:  0,
        summary: 'No catalogue metadata for this key — frontend out of date or DB has a manually-added flag.',
        icon:    <SettingOutlined />,
        enabled: r.enabled,
        activatedAt: r.activatedAt,
        activatedBy: r.activatedBy,
        hasRow:  true,
      }));
    return [...catalogueRows, ...orphanRows];
  }, [rowByKey, rows]);

  const areas = useMemo(() => {
    const set = new Set<string>();
    composed.forEach(r => set.add(r.area));
    return Array.from(set).sort();
  }, [composed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return composed.filter(r => {
      if (areaTab !== 'all' && r.area !== areaTab) return false;
      if (!q) return true;
      return r.key.toLowerCase().includes(q)
        || r.name.toLowerCase().includes(q)
        || r.summary.toLowerCase().includes(q);
    });
  }, [composed, search, areaTab]);

  const handleToggle = async (key: string, nextEnabled: boolean) => {
    if (!selectedCondoId) return;
    setToggling(t => ({ ...t, [key]: true }));
    try {
      if (nextEnabled) {
        await apiService.activateCondoFeature({
          condoId:     selectedCondoId,
          featureKey:  key,
          activatedBy: user?.id || '',
          notes:       'Toggled from /admin/features',
        });
      } else {
        await apiService.deactivateCondoFeature({
          condoId:    selectedCondoId,
          featureKey: key,
        });
      }
      // Re-fetch to keep activatedAt/activatedBy fresh
      const refreshed = await apiService.getCondoFeatures(selectedCondoId);
      setRows(refreshed);
      staticApi.message.success(`${key} ${nextEnabled ? 'activated' : 'deactivated'}`);
    } catch (e: any) {
      staticApi.message.error(`Toggle failed: ${e?.message || 'unknown error'}`);
    } finally {
      setToggling(t => ({ ...t, [key]: false }));
    }
  };

  if (!isSuperAdmin) {
    return (
      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        message="Super-admin access required"
        description="Feature toggles are a super-admin-only surface. Contact your CSM if you need a capability enabled."
        style={{ maxWidth: 600, margin: '40px auto' }}
      />
    );
  }

  const summary = {
    total:    composed.length,
    enabled:  composed.filter(r => r.enabled).length,
    disabled: composed.filter(r => !r.enabled).length,
  };

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          <SettingOutlined style={{ marginRight: 10, color: '#1890ff' }} />
          Feature Toggles
        </Title>
        <Text type="secondary">
          Per-condo capability gates. Activating a feature here grants the selected condo access to the underlying capability on the next request. Each toggle is logged with your user-id and a timestamp.
        </Text>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          showSearch
          placeholder="Select a condo"
          style={{ width: 280 }}
          value={selectedCondoId}
          onChange={setSelectedCondoId}
          optionFilterProp="children"
          prefix={<BankOutlined />}
        >
          {condos?.map((c: any) => (
            <Option key={c.id} value={c.id}>{c.name}</Option>
          ))}
        </Select>
        <Input
          allowClear
          placeholder="Search by name, key, or description…"
          prefix={<SearchOutlined />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 320 }}
        />
        <Tag color="blue">{summary.enabled} enabled</Tag>
        <Tag>{summary.disabled} disabled</Tag>
        <Tag color="default">{summary.total} total</Tag>
        <Tooltip title="Reload from backend">
          <Button icon={<ReloadOutlined />} onClick={() => selectedCondoId && setSelectedCondoId(c => c)} />
        </Tooltip>
      </Space>

      {!selectedCondoId ? (
        <Empty description="Pick a condo to see its feature toggles." />
      ) : (
        <Card>
          <Tabs
            activeKey={areaTab}
            onChange={setAreaTab}
            items={[
              { key: 'all', label: `All (${composed.length})` },
              ...areas.map(a => ({
                key: a,
                label: (
                  <Space size={4}>
                    {AREA_ICONS[a as FeatureArea] ?? <SettingOutlined />}
                    {a} ({composed.filter(r => r.area === a).length})
                  </Space>
                ),
              })),
            ]}
          />
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table
              rowKey="key"
              dataSource={filtered}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              size="middle"
              columns={[
                {
                  title: 'Feature',
                  dataIndex: 'name',
                  key: 'name',
                  render: (_: any, r: any) => (
                    <Space direction="vertical" size={0}>
                      <Space>
                        <span style={{ color: '#1890ff' }}>{r.icon}</span>
                        <Text strong>{r.name}</Text>
                        {r.sprint > 0 && (
                          <Tag color={r.sprint <= 38 ? 'green' : 'blue'} style={{ fontSize: 11 }}>
                            Sprint {r.sprint}
                          </Tag>
                        )}
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>{r.summary}</Text>
                      <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.key}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Area',
                  dataIndex: 'area',
                  key: 'area',
                  width: 140,
                  render: (a: FeatureArea) => <Tag>{a}</Tag>,
                  filters: areas.map(a => ({ text: a, value: a })),
                  onFilter: (v: any, r: any) => r.area === v,
                },
                {
                  title: 'Last Activated',
                  key: 'activatedAt',
                  width: 160,
                  render: (_: any, r: any) => r.activatedAt
                    ? <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(r.activatedAt)}</Text>
                    : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
                },
                {
                  title: 'Enabled',
                  key: 'enabled',
                  width: 110,
                  align: 'right' as const,
                  render: (_: any, r: any) => (
                    <Popconfirm
                      title={r.enabled ? 'Deactivate this feature?' : 'Activate this feature?'}
                      description={r.enabled
                        ? `Disabling "${r.name}" will revoke access for this condo on the next request.`
                        : `Enabling "${r.name}" grants this condo access on the next request.`}
                      okText="Yes"
                      cancelText="Cancel"
                      onConfirm={() => handleToggle(r.key, !r.enabled)}
                    >
                      <Switch
                        checked={r.enabled}
                        loading={toggling[r.key]}
                      />
                    </Popconfirm>
                  ),
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default FeatureToggles;
